import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '../../../hooks';
import { canEditResume } from '../../../shared/persona';
import { fetchDirectory, type DirectoryData, type DirectoryMember } from '../client';
import './styles.css';
import './console.css';

function initial(name: string): string {
  return name.trim().slice(0, 1) || '同';
}

function MemberCard({ member }: { member: DirectoryMember }) {
  const body = (
    <>
      {member.meta.thumb && !member.locked ? (
        <img src={`/resume/${member.slug}/${member.meta.thumb}`} alt="" loading="lazy" />
      ) : (
        <span className="resume-member-avatar" aria-hidden="true">
          {initial(member.meta.name)}
        </span>
      )}
      <div className="resume-member-body">
        <h3>
          {member.meta.name}
          {member.isCurrent ? null : <span className="resume-alumni">已毕业</span>}
          {member.pending ? <span className="resume-alumni">新版审核中</span> : null}
          {member.locked ? <span className="resume-alumni">未上线</span> : null}
        </h3>
        <p className="resume-meta">
          {[member.meta.grade, member.meta.direction].filter(Boolean).join(' · ') || '资料待完善'}
        </p>
        <p className="resume-member-summary">
          {member.locked
            ? member.pending
              ? '简历正在审核，暂不可查看。'
              : '简历还没有上线。'
            : member.meta.summary}
        </p>
        {member.locked && member.pendingId ? (
          <a
            className="resume-view"
            href={`/resume-preview/${member.pendingId}/`}
            target="_blank"
            rel="noreferrer"
          >
            预览我的待审核版本 →
          </a>
        ) : null}
        {member.locked && member.isSelf && !member.pendingId ? (
          <Link className="resume-view" to="/account/resume">
            去我的简历 →
          </Link>
        ) : null}
      </div>
    </>
  );

  if (member.locked) {
    return (
      <div className="resume-member resume-member-locked" data-slug={member.slug}>
        {body}
      </div>
    );
  }
  return (
    <a
      className="resume-member"
      data-slug={member.slug}
      href={`/resume/${member.slug}/`}
      target="_blank"
      rel="noreferrer"
    >
      {body}
    </a>
  );
}

export default function ResumeDirectory() {
  const me = useMe();
  const directory = useQuery<DirectoryData>({
    queryKey: ['resume-directory'],
    queryFn: fetchDirectory,
  });
  const members = directory.data?.members ?? [];
  const liveCount = members.filter((member) => !member.locked).length;

  return (
    <div className="resume-directory">
      <div className="page-head">
        <div>
          <div className="eyebrow">People / 课题组</div>
          <h1>课题组简历</h1>
        </div>
        <div className="resume-actions">
          {canEditResume(me.data?.persona ?? 'visitor') ? (
            <Link className="btn btn-ghost btn-sm" to="/account/resume">
              我的简历
            </Link>
          ) : null}
          <a className="btn btn-ghost btn-sm" href="/resume" target="_blank" rel="noreferrer">
            公开简历页
          </a>
        </div>
      </div>
      <p className="muted">已上线的简历可以直接点开；审核中或未上线的只显示基本信息。</p>
      {directory.isLoading ? <div className="empty">正在读取成员…</div> : null}
      {!directory.isLoading && members.length === 0 ? (
        <div className="empty">还没有成员建立简历资料。</div>
      ) : null}
      <div className="resume-member-grid">
        {members.map((member) => (
          <MemberCard key={member.slug} member={member} />
        ))}
      </div>
      {members.length > 0 ? (
        <p className="muted resume-directory-count">
          已上线 {liveCount} / {members.length} 人
        </p>
      ) : null}
    </div>
  );
}
